import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  SendToDocumentStepDataName,
  SaveToResultsStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, isAnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {
  getOutputDataReferenceForDataFrame,
  getOutputStepDataForChart,
} from 'analysis-tool/data/getOutputStepDataForDataFrame';
import AnalysisOutputSelector from 'analysis-tool/outputs/AnalysisOutputSelector';
import {
  getDocumentSectionLayoutConfig,
  getUpdatedDocumentLayoutConfigurationFromNewSelectedOutputs,
} from 'analysis-tool/outputs/ConfigureDocumentUtil';
import {EXCEEDED_MAX_NUM_OUTPUTS_MESSAGE, MAX_NUM_OUTPUTS} from 'analysis-tool/outputs/Constants';
import OutputDocumentLayout from 'analysis-tool/outputs/OutputDocumentLayout';
import {isSendToDocumentEnabled} from 'analysis-tool/outputs/OutputsUtil';
import {
  AnalysisOutput,
  AnalysisOutputType,
  ChartOutputConfig,
  OutputDocumentLayoutConfiguration,
  OutputDocumentLayoutItemType,
} from 'analysis-tool/outputs/Types';
import {DatasetOutputConfig} from 'analysis-tool/outputs/Types';
import AnalysisTemplateInputCard from 'analysis-tool/ui/AnalysisTemplateInputCard';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible, assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import {AddStepWithParentStepReference, StepReference} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getOutputDataForLineageNode} from 'pipelines/PipelineViewUtils';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import TextArea from 'ui-library/bs/TextArea';
import * as Colors from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import TextInput from 'ui-library/inputs/TextInput';
import {StepObject, stepperStyles, StepperV2} from 'ui-library/stepper/Stepper';
import {withLoadingScreen} from 'util/Promises';

import * as AnalysisTemplateActions from './AnalysisTemplateActions';
import {UNSET_CONFIG_VARIABLE} from './AnalysisTemplateDAO';
import {
  InputTableConfigState,
  InputTableType,
  getDefaultTemplateConfigStateForAnalysis,
  getTemplateDataForLineageNode,
} from './AnalysisTemplateUtil';
import {AnalysisTemplateConfigState} from './AnalysisTemplateUtil';
import CreateAnalysisTemplatePreview from './CreateAnalysisTemplatePreview';
import StartingTablesInfo from './StartingTablesInfo';

/* @package */
export const TemplateConfigState = makeUseGlobalClientState<AnalysisTemplateConfigState | null>(null);

/* @package */
export const CREATE_TEMPLATE_WITH_FLAG_ROWS_WARNING =
  'Analysis contains manually flagged rows. Those steps will not be part of the created template.';

function getCreateTemplateWarnings(analysis: AnalysisGraphQL): Array<string> {
  const messages: Array<string> = [];
  if (analysis.execution.steps.some((step) => step.definition.stepType === PipelineStepType.FLAG_ROWS)) {
    messages.push(CREATE_TEMPLATE_WITH_FLAG_ROWS_WARNING);
  }

  return messages;
}

async function submitTemplateCreation(
  templateConfigState: AnalysisTemplateConfigState,
  analysisId: string,
  steps: ReadonlyArray<PipelineStep>
): Promise<void> {
  assert(templateConfigState.name.trim().length !== 0, 'Template name must be set');
  assert(templateConfigState.templateCollectionId != null, 'Template collection must be set');

  const saveToResultsIndices: Array<number> = [];
  const exportToDatasetIndices: Array<number> = [];
  const chartIndices: Array<number> = [];
  const outputStepParams: Array<AddStepWithParentStepReference<PipelineStepType>> =
    templateConfigState.selectedOutputs
      .map((output, index) => {
        if (output.outputConfig.type === AnalysisOutputType.DATASET) {
          exportToDatasetIndices.push(index);
          const {stepDataName, stepDataIndex: fromStepDataIndex} = getOutputDataReferenceForDataFrame(
            output.outputConfig.step,
            output.outputConfig.dataFrame.id
          )!;
          return {
            step: {
              stepType: PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
              stepVersion: 0,
              stepConfig: {
                folderId: UNSET_CONFIG_VARIABLE,
                datasetName: UNSET_CONFIG_VARIABLE,
              },
              stepInputParents: {
                [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
                  {
                    stepReference: {
                      type: StepReference.STEP_ID,
                      stepId: output.outputConfig.step.id,
                    },
                    stepDataName,
                    fromStepDataIndex,
                  },
                ],
              },
            },
          };
        } else if (output.outputConfig.type === AnalysisOutputType.RESULT) {
          saveToResultsIndices.push(index);
          const dataMapping = output.outputConfig.newDataMapping;
          const {stepDataName, stepDataIndex: fromStepDataIndex} = getOutputDataReferenceForDataFrame(
            output.outputConfig.step,
            output.outputConfig.dataFrame.id
          )!;
          return {
            step: {
              stepType: PipelineStepType.SAVE_TO_RESULTS,
              stepVersion: 2,
              stepConfig: {
                schemaId: dataMapping.resultSchema!.id,
                columnMapping: dataMapping.mappings,
                targetSpecificMapping: output.outputConfig.targetSpecificMappingState,
              },
              stepInputParents: {
                [SaveToResultsStepDataName.INPUT_DATASET]: [
                  {
                    stepReference: {
                      type: StepReference.STEP_ID,
                      stepId: output.outputConfig.step.id,
                    },
                    stepDataName,
                    fromStepDataIndex,
                  },
                ],
              },
            },
          };
        } else {
          assert(output.outputConfig.type === AnalysisOutputType.CHART, 'Unexpected output type', {
            type: output.outputConfig.type,
          });
          chartIndices.push(index);
          return null;
        }
      })
      .filter((maybeStepParam) => maybeStepParam != null) as Array<
      AddStepWithParentStepReference<PipelineStepType>
    >;
  if (templateConfigState.selectedOutputs.length > 0 && isSendToDocumentEnabled()) {
    outputStepParams.push({
      step: {
        stepType: PipelineStepType.SEND_TO_DOCUMENT,
        stepVersion: 0,
        stepConfig: {
          documentId: UNSET_CONFIG_VARIABLE,
          sectionLayout: getDocumentSectionLayoutConfig(
            chartIndices.map((index) => templateConfigState.selectedOutputs[index]),
            exportToDatasetIndices.map((index) => templateConfigState.selectedOutputs[index]),
            saveToResultsIndices.map((index) => templateConfigState.selectedOutputs[index]),
            templateConfigState.outputDocumentLayoutConfiguration
          ),
          // TODO(BNCH-116986): GeneratedPipelineStepShapes.ts does not accomodate UNSET value
          sectionIndex: UNSET_CONFIG_VARIABLE as unknown as number,
          lockResultTables: templateConfigState.lockResultTables ?? false,
          hideAnalysisChip: false,
        },
        stepInputParents: {
          [SendToDocumentStepDataName.INPUT_SAVED_RESULTS]: outputStepParams
            .map((outputStep, index) => [outputStep, index] as const)
            .filter(([outputStep, _]) => outputStep.step.stepType === PipelineStepType.SAVE_TO_RESULTS)
            .map(([_, index]) => ({
              stepReference: {type: StepReference.INDEX, index},
              stepDataName: SaveToResultsStepDataName.SAVED_RESULTS,
            })),
          [SendToDocumentStepDataName.INPUT_DATASETS]: outputStepParams
            .map((outputStep, index) => [outputStep, index] as const)
            .filter(
              ([outputStep, _]) => outputStep.step.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET
            )
            .map(([_, index]) => ({
              stepReference: {type: StepReference.INDEX, index},
              stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
            })),
          [SendToDocumentStepDataName.INPUT_CHARTS]: templateConfigState.selectedOutputs
            .filter((output) => output.outputConfig.type === AnalysisOutputType.CHART)
            .map((output) => {
              const outputStepData = getOutputStepDataForChart(
                output.outputConfig.step,
                (output.outputConfig as ChartOutputConfig).chart.id
              );

              const chartIndexInStep =
                outputStepData?.charts.findIndex(
                  (c) =>
                    !isInaccessible(c) &&
                    assertIsAccessible(c).id === (output.outputConfig as ChartOutputConfig).chart.id
                ) ?? 0;

              return {
                stepReference: {type: StepReference.STEP_ID, stepId: output.outputConfig.step.id},
                stepDataName: outputStepData!.name,
                fromStepDataIndex: chartIndexInStep,
              };
            }),
        },
      },
    });
  }

  await withLoadingScreen(
    AnalysisTemplateActions.createAnalysisTemplate({
      name: templateConfigState.name,
      createdFromPipelineId: analysisId,
      dataFrameVariables: templateConfigState.inputTables
        .filter((table) => table.inputType === InputTableType.VARIABLE)
        .map((inputTable) => ({
          ...getTemplateDataForLineageNode(inputTable.lineageNode, steps),
          description: inputTable.description,
        })),
      templateCollectionId: templateConfigState.templateCollectionId,
      outputSteps: outputStepParams,
    })
  );
}

function DatasetList({datasetOutputs}: {datasetOutputs: Array<AnalysisOutput>}): JSX.Element {
  return (
    <div className={css(styles.datasetOutputList)}>
      {datasetOutputs.map((datasetOutput, index) => (
        <span key={index}>
          <BenchlingIcon icon={datasetOutput.outputConfig.icon} className="u-mini-spacing-left" />
          <span className="u-mini-spacing-left">
            {(datasetOutput.outputConfig as DatasetOutputConfig).outputName}
          </span>
        </span>
      ))}
    </div>
  );
}

export default function AnalysisTemplateConfigurationView({
  analysis,
  clientStateId,
  onChangeSelectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.TEMPLATE_CONFIGURATION>): JSX.Element {
  assert(isAnalysisGraphQL(analysis), 'AnalysisTemplateConfigurationView only supports analyses.');
  const [maybeTemplateConfigState, setTemplateConfigState] = TemplateConfigState.useGlobalClientState(
    analysis.id
  );
  const templateConfigState = maybeTemplateConfigState ?? getDefaultTemplateConfigStateForAnalysis(analysis);

  function handleChangeInputTableType(index: number, newType: InputTableType): void {
    const newTemplateState = {
      ...templateConfigState,
      inputTables: templateConfigState.inputTables.map((editingInputTable, i) =>
        i === index ? {...editingInputTable, inputType: newType} : editingInputTable
      ),
    };
    setTemplateConfigState(newTemplateState);
  }

  function handleChangeInputTableConfigState(
    index: number,
    newInputTableConfigState: InputTableConfigState
  ): void {
    setTemplateConfigState({
      ...templateConfigState,
      inputTables: templateConfigState.inputTables.map((editingInputTable, i) =>
        i === index ? newInputTableConfigState : editingInputTable
      ),
    });
  }

  function handleSelectTemplateCollectionId({id: templateCollectionId}: {id: string}): void {
    setTemplateConfigState({
      ...templateConfigState,
      templateCollectionId,
    });
  }

  function handleChangeSelectedOutputs(newSelectedOutputs: Array<AnalysisOutput>): void {
    setTemplateConfigState({
      ...templateConfigState,
      selectedOutputs: newSelectedOutputs,
      outputDocumentLayoutConfiguration: getUpdatedDocumentLayoutConfigurationFromNewSelectedOutputs(
        templateConfigState.outputDocumentLayoutConfiguration,
        templateConfigState.selectedOutputs,
        newSelectedOutputs
      ),
    });
  }

  function handleChangeDocumentLayoutConfiguration(
    newDocumentLayoutConfiguration: OutputDocumentLayoutConfiguration
  ): void {
    let newConfiguration: OutputDocumentLayoutConfiguration;
    if (newDocumentLayoutConfiguration[0].type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
      newConfiguration = newDocumentLayoutConfiguration;
    } else {
      // Move the first DOCUMENT_SECTION_TITLE to the beginning of the configuration to force all DOCUMENT_CONTENT
      // to be contained within a section even if the user tries to drag the content above the section title
      const firstSectionTitleIndex = newDocumentLayoutConfiguration.findIndex(
        (item) => item.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE
      );
      assert(firstSectionTitleIndex !== -1, 'Did not find any section title.');
      newConfiguration = [
        newDocumentLayoutConfiguration[firstSectionTitleIndex],
        ...newDocumentLayoutConfiguration.filter((item, idx) => idx !== firstSectionTitleIndex),
      ];
    }
    setTemplateConfigState({
      ...templateConfigState,
      outputDocumentLayoutConfiguration: newConfiguration,
    });
  }

  function closeTemplateConfigurationView(): void {
    onChangeSelectedItem(getDefaultMainViewItemForAnalysis(analysis));
    TemplateConfigState.deleteState(analysis.id);
  }

  async function handleSubmitTemplateCreation(): Promise<void> {
    await submitTemplateCreation(templateConfigState, analysis.id, analysis.steps);
    closeTemplateConfigurationView();
  }

  const warnings = getCreateTemplateWarnings(analysis);
  const chartOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.CHART
  );
  const resultOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.RESULT
  );
  const datasetOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.DATASET
  );
  const documentSectionLayout = getDocumentSectionLayoutConfig(
    chartOutputs,
    datasetOutputs,
    resultOutputs,
    templateConfigState.outputDocumentLayoutConfiguration
  );

  const hasTooManyOutputs = templateConfigState.selectedOutputs.length > MAX_NUM_OUTPUTS;

  const stepObjects: Array<StepObject> = [
    {
      key: 'template-details',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Setup template details</div>,
      content: (
        <div className={classNames(css(stepperStyles.defaultStepContent), css(detailsSectionStyles.row))}>
          <div>
            <FieldLabel label="Template name" />
            <TextInput
              onChange={(e) => {
                setTemplateConfigState({...templateConfigState, name: e.target.value});
              }}
              placeholder="Enter a name for this template"
              value={templateConfigState.name}
              dataTestAttrs={{
                component: 'AnalysisTemplateView',
                element: 'templateNameInput',
              }}
            />
          </div>
          <div>
            <FieldLabel label="Template collection" />
            <TemplateCollectionSelector
              searchable={true}
              value={templateConfigState.templateCollectionId}
              onChange={handleSelectTemplateCollectionId}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'inputs',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Setup inputs</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <div className={css(inputTablesSectionStyles.header)}>Starting Tables</div>
          <div className={css(inputTablesSectionStyles.helpText)}>
            Choose how starting tables will be created when this template is used.
            <StartingTablesInfo />
          </div>
          <div className={css(inputTablesSectionStyles.wrapper)}>
            {templateConfigState.inputTables.map((inputTable, index) => (
              <AnalysisTemplateInputCard
                key={index}
                title={
                  getOutputDataForLineageNode(
                    inputTable.lineageNode,
                    analysis.steps,
                    PipelineStepDataKind.DATASET
                  ).name
                }
              >
                <RadioButtonSelector
                  selectedValue={inputTable.inputType}
                  options={[
                    {
                      label: 'Variable',
                      value: InputTableType.VARIABLE,
                      dataTestAttrs: {
                        component: 'AnalysisTemplateView',
                        element: 'variableRadioOption',
                        index: String(index),
                      },
                    },
                    {
                      label: 'Static',
                      value: InputTableType.STATIC,
                      dataTestAttrs: {
                        component: 'AnalysisTemplateView',
                        element: 'staticRadioOption',
                        index: String(index),
                      },
                    },
                  ]}
                  onChange={(newInputType) => {
                    handleChangeInputTableType(index, newInputType);
                  }}
                  isVertical={true}
                />
                {inputTable.inputType === InputTableType.VARIABLE && (
                  <TextArea
                    onChange={(e) => {
                      handleChangeInputTableConfigState(index, {
                        ...inputTable,
                        description: e.target.value,
                      });
                    }}
                    placeholder="What kind of data should users provide in this table?"
                    value={inputTable.description}
                    {...getTestProps({
                      component: 'AnalysisTemplateView',
                      element: 'tableDescriptionInput',
                      index: String(index),
                    })}
                  />
                )}
              </AnalysisTemplateInputCard>
            ))}
          </div>
        </div>
      ),
    },
  ];

  stepObjects.push({
    key: 'select-items-to-export',
    title: <div className={css(stepperStyles.defaultStepTitle)}>Select items to export</div>,
    content: (
      <div className={css(stepperStyles.defaultStepContent)}>
        <AnalysisOutputSelector
          pipeline={analysis}
          clientStateId={clientStateId}
          onUpdateSelectedOutputs={handleChangeSelectedOutputs}
          selectedOutputs={templateConfigState.selectedOutputs}
        />
        {resultOutputs.length > 0 && (
          <div className={css(styles.checkboxContainer)}>
            <CheckboxInput
              id="lock-result-tables-template"
              label="Lock result tables"
              helpText="Prevent edits to result tables. Results in locked tables can still be archived."
              checked={templateConfigState.lockResultTables ?? false}
              onChange={(e) => {
                setTemplateConfigState({
                  ...templateConfigState,
                  lockResultTables: e.target.checked,
                });
              }}
            />
          </div>
        )}
        {hasTooManyOutputs && (
          <div className={css(styles.sectionBanner)}>
            <InlineBanner bannerType={InlineBannerType.ERROR} text={EXCEEDED_MAX_NUM_OUTPUTS_MESSAGE} />
          </div>
        )}
      </div>
    ),
  });

  if (datasetOutputs.length > 0) {
    stepObjects.push({
      key: 'review-datasets',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Review datasets</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <div className={css(styles.description)}>
            These datasets will be saved in the same folder as the source document when the template is run
            from a worksheet.
          </div>
          <div className={css(styles.datasetOutputList)}>
            <DatasetList datasetOutputs={datasetOutputs} />
          </div>
        </div>
      ),
    });
  }

  if (templateConfigState.selectedOutputs.length > 0 && isSendToDocumentEnabled()) {
    stepObjects.push({
      key: 'configure-document-inserts',
      title: <div className={css(stepperStyles.defaultStepTitle)}>Configure document inserts</div>,
      content: (
        <div className={css(stepperStyles.defaultStepContent)}>
          <div className={css(styles.description)}>
            If run from a worksheet, these sections will be added to a new step.
          </div>
          <OutputDocumentLayout
            documentLayoutConfiguration={templateConfigState.outputDocumentLayoutConfiguration}
            onSetDocumentLayoutConfiguration={handleChangeDocumentLayoutConfiguration}
          />
        </div>
      ),
    });
  }

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AnalysisTemplateView', element: 'root'})}
    >
      <StepConfigurationViewTemplate
        clientStateId={clientStateId}
        dataFrames={[]}
        disableFinalizeButton={
          templateConfigState.name.trim().length === 0 ||
          templateConfigState.templateCollectionId == null ||
          hasTooManyOutputs ||
          // or if they are exporting results but have an empty section
          (resultOutputs.length > 0 &&
            documentSectionLayout.some((section) => section.inputReferences.length === 0))
        }
        finalizeButtonText="Create"
        // We use withLoadingScreen instead as a loading indicator
        isFinalizing={false}
        messages={(warnings ?? []).map((warning) => ({bannerType: InlineBannerType.WARNING, text: warning}))}
        onCancel={closeTemplateConfigurationView}
        onFinalize={handleSubmitTemplateCreation}
        renderAdditionalPreviewContent={() => (
          <CreateAnalysisTemplatePreview
            pipeline={analysis}
            clientStateId={clientStateId}
            outputs={templateConfigState.selectedOutputs}
            documentLayoutConfiguration={templateConfigState.outputDocumentLayoutConfiguration}
          />
        )}
        title="Configure as template"
      >
        <StepperV2 steps={stepObjects} />
      </StepConfigurationViewTemplate>
    </div>
  );
}

const GRID_WITH_TWO_COLUMNS = {display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)'} as const;
const styles = StyleSheet.create({
  datasetOutputList: {display: 'flex', flexDirection: 'column', gap: 6, ...FONT_PROXIMA_13, marginTop: 20},
  description: {marginBottom: 8, ...FONT_PROXIMA_13},
  sectionBanner: {marginTop: '10px'},
  wrapper: {height: '100%'},
  checkboxContainer: {marginTop: 16, marginBottom: 8},
});
const detailsSectionStyles = StyleSheet.create({row: {...GRID_WITH_TWO_COLUMNS, gap: 16}});
const inputTablesSectionStyles = StyleSheet.create({
  header: {
    textTransform: 'uppercase',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Colors.GRAY_DARKER,
  },
  helpText: {paddingBottom: 8, paddingTop: 8},
  wrapper: {...GRID_WITH_TWO_COLUMNS, gap: 8},
});
